import React, { useState } from "react";
import { Accordion, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import InputMask from "react-input-mask";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import RequiredIcon from '../../../../components/RequiredIcon';
import { StateDropdown } from "../../../../components/StateDropdown";
import { ControlOwnerRoleEnum, MerchantOwner, MerchantProcessor as Merchant } from '../../../../models/Client';
import { onboardMerchantAction, syncMerchantOwnersAction } from '../../../../redux/actions/clients/merchants';

export interface IControlOwnersModalProps {
    show: boolean;
    merchant: Merchant;
    onHide: () => void;
    actionToken: string;
    // "onboard": merchant hasn't been onboarded yet - owners are saved locally, then the merchant
    // is onboarded (owners get sent inline on Create Submerchant).
    // "sync": merchant is already onboarded - owner changes are diffed and pushed live to Worldpay.
    mode?: 'onboard' | 'sync';
}

// Worldpay's Owner "type" field must be unique per owner and only offers this many slots
// (ControlOwner + BeneficialOwner1-9) - cap the list to match.
const MAX_OWNERS = 10;

const newOwner = (): MerchantOwner => new MerchantOwner();

const ownerTypeLabel = (index: number): string => index === 0 ? "Control Owner" : `Beneficial Owner ${index}`;

// The API serializes `role` as its enum name (e.g. "ExecutiveDirector"), but the <option> values
// below are the enum's numeric form - normalize on load so the select shows the saved selection.
const normalizeRole = (owner: MerchantOwner): MerchantOwner => ({
    ...owner,
    role: ControlOwnerRoleEnum[owner.role as unknown as keyof typeof ControlOwnerRoleEnum] ?? owner.role
});

const ControlOwnersModal = ({ show, merchant, onHide, actionToken, mode = 'onboard' }: IControlOwnersModalProps) => {
    const dispatch = useDispatch();
    const [validated, setValidated] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [activeKey, setActiveKey] = useState<string>("0");
    const [owners, setOwners] = useState<Array<MerchantOwner>>(
        merchant?.owners && merchant.owners.length > 0 ? merchant.owners.map(o => normalizeRole({ ...o })) : [newOwner()]
    );

    const totalPercentage = owners.reduce((sum, o) => sum + (Number(o.ownershipPercentage) || 0), 0);

    const updateOwner = (index: number, changes: Partial<MerchantOwner>) => {
        setOwners(prev => prev.map((o, i) => i === index ? { ...o, ...changes } : o));
    };

    const addOwner = () => {
        setOwners(prev => [...prev, newOwner()]);
        setActiveKey(`${owners.length}`);
    };

    const removeOwner = (index: number) => setOwners(prev => prev.filter((_, i) => i !== index));

    const handleSubmit = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
        const form = event.currentTarget;

        if (form.checkValidity() === false) {
            setValidated(true);
            return;
        }

        if (totalPercentage > 100) {
            setError("Total ownership percentage cannot exceed 100%.");
            return;
        }

        setError("");
        setIsSaving(true);

        const payload = owners.map(o => ({
            ...o,
            phoneNumber: o.phoneNumber.replace(/\D/g, ""),
            ssn: o.ssn?.replace(/-/g, "")
        }));

        const saveAction = mode === 'sync'
            ? (dispatch(syncMerchantOwnersAction(merchant.msbId!, payload, actionToken) as any) as Promise<any>)
            : (dispatch(onboardMerchantAction(merchant.msbId!, actionToken, payload) as any) as Promise<any>);

        saveAction
            .then(() => {
                setIsSaving(false);
                onHide();
            })
            .catch(() => {
                setIsSaving(false);
                setError(mode === 'sync'
                    ? "Could not save the owner details. Please review the fields above or check the notification for details."
                    : "Could not complete onboarding. Please review the fields above or check the notification for details.");
            });
    };

    return (
        <Modal size="lg" show={show} onHide={onHide} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Control Owners</Modal.Title>
            </Modal.Header>
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Modal.Body>
                    <p style={{ marginBottom: '15px' }}>{mode === 'sync'
                        ? "Changes to owners below are saved to Worldpay/Vantiv immediately."
                        : "Worldpay/Vantiv requires at least one owner's details before onboarding can proceed."}</p>
                    <Accordion className="agreement-form" activeKey={activeKey}>
                        {owners.map((owner, index) => (
                            <Card key={index}>
                                <Accordion.Toggle
                                    as={Card.Header}
                                    eventKey={`${index}`}
                                    className="agreement_toggle_header"
                                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                    onClick={() => setActiveKey(activeKey === `${index}` ? "" : `${index}`)}
                                >
                                    <span>
                                        <FontAwesomeIcon icon={faChevronDown} />
                                        Owner {index + 1} &mdash; {ownerTypeLabel(index)}
                                        {(owner.firstName || owner.lastName) ? ` (${owner.firstName} ${owner.lastName})`.trimEnd() : ""}
                                    </span>
                                    {index > 0 &&
                                        <Button variant="outline-danger" size="sm" type="button" onClick={(e: any) => { e.stopPropagation(); removeOwner(index); }}>Remove</Button>}
                                </Accordion.Toggle>
                                <Accordion.Collapse eventKey={`${index}`}>
                                    <Card.Body>
                                        <Row>
                                            <Col lg={4} sm={12}>
                                                <Form.Group controlId={`role_${index}`}>
                                                    <Form.Label><RequiredIcon />Job title</Form.Label>
                                                    <Form.Control required as="select" value={owner.role ?? ""} onChange={(e: any) => updateOwner(index, { role: e.target.value })}>
                                                        <option value="">Select</option>
                                                        <option value={ControlOwnerRoleEnum.ExecutiveDirector}>Executive Director</option>
                                                        <option value={ControlOwnerRoleEnum.CEO}>CEO</option>
                                                        <option value={ControlOwnerRoleEnum.CFO}>CFO</option>
                                                        <option value={ControlOwnerRoleEnum.BoardPresidentChair}>Board President/Chair</option>
                                                        <option value={ControlOwnerRoleEnum.Treasurer}>Treasurer</option>
                                                    </Form.Control>
                                                    <Form.Control.Feedback type="invalid">Please select the owner's job title.</Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                            <Col lg={4} sm={12}>
                                                <Form.Group controlId={`dob_${index}`}>
                                                    <Form.Label><RequiredIcon />Date of birth</Form.Label>
                                                    <Form.Control required type="date" value={owner.dob ?? ""} onChange={(e: any) => updateOwner(index, { dob: e.target.value })} />
                                                    <Form.Control.Feedback type="invalid">Please enter a date of birth.</Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                            <Col lg={4} sm={12}>
                                                <Form.Group controlId={`ownershipPercentage_${index}`}>
                                                    <Form.Label><RequiredIcon />Ownership %</Form.Label>
                                                    <Form.Control required type="number" min={1} max={100} value={owner.ownershipPercentage || ""} onChange={(e: any) => updateOwner(index, { ownershipPercentage: Number(e.target.value) })} />
                                                    <Form.Control.Feedback type="invalid">Please enter an ownership percentage (1-100).</Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col lg={5} sm={12}>
                                                <Form.Group controlId={`firstName_${index}`}>
                                                    <Form.Label><RequiredIcon />First name</Form.Label>
                                                    <Form.Control required type="input" maxLength={15} value={owner.firstName} onChange={(e: any) => updateOwner(index, { firstName: e.target.value })} placeholder="Enter first name" />
                                                    <Form.Control.Feedback type="invalid">Please enter a first name.</Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                            <Col lg={2} sm={12}>
                                                <Form.Group controlId={`middleInitial_${index}`}>
                                                    <Form.Label>MI</Form.Label>
                                                    <Form.Control type="input" maxLength={1} value={owner.middleInitial ?? ""} onChange={(e: any) => updateOwner(index, { middleInitial: e.target.value })} />
                                                </Form.Group>
                                            </Col>
                                            <Col lg={5} sm={12}>
                                                <Form.Group controlId={`lastName_${index}`}>
                                                    <Form.Label><RequiredIcon />Last name</Form.Label>
                                                    <Form.Control required type="input" maxLength={25} value={owner.lastName} onChange={(e: any) => updateOwner(index, { lastName: e.target.value })} placeholder="Enter last name" />
                                                    <Form.Control.Feedback type="invalid">Please enter a last name.</Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col lg={6} sm={12}>
                                                <Form.Group controlId={`email_${index}`}>
                                                    <Form.Label><RequiredIcon />Email</Form.Label>
                                                    <Form.Control required type="email" maxLength={64} value={owner.email} onChange={(e: any) => updateOwner(index, { email: e.target.value })} placeholder="email@address.com" />
                                                    <Form.Control.Feedback type="invalid">Please enter an email address.</Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                            <Col lg={6} sm={12}>
                                                <Form.Group controlId={`phoneNumber_${index}`}>
                                                    <Form.Label><RequiredIcon />Phone number</Form.Label>
                                                    <InputMask required mask="(999) 999-9999" className="form-control" value={owner.phoneNumber} onChange={(e: any) => updateOwner(index, { phoneNumber: e.target.value })} placeholder="(999) 999-9999" />
                                                    <Form.Control.Feedback type="invalid">Please enter a phone number.</Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col lg={6} sm={12}>
                                                <Form.Group controlId={`ssn_${index}`}>
                                                    <Form.Label>
                                                        <RequiredIcon />SSN
                                                        {owner.msbId && !owner.ssn &&
                                                            <span className="text-muted fs-mask" style={{ fontWeight: 'normal', fontSize: '0.85em', marginLeft: '6px' }}>(Cleared for security. Please re-enter)</span>}
                                                    </Form.Label>
                                                    <InputMask required mask="999-99-9999" pattern="\d{3}-\d{2}-\d{4}" className="form-control fs-mask" value={owner.ssn ?? ""} onChange={(e: any) => updateOwner(index, { ssn: e.target.value })} placeholder="999-99-9999" />
                                                    <Form.Control.Feedback type="invalid">Please enter a valid SSN.</Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col lg={6} sm={12}>
                                                <Form.Group controlId={`addressLine1_${index}`}>
                                                    <Form.Label><RequiredIcon />Home address line 1</Form.Label>
                                                    <Form.Control required type="input" maxLength={40} value={owner.addressLine1} onChange={(e: any) => updateOwner(index, { addressLine1: e.target.value })} placeholder="Enter address line 1" />
                                                    <Form.Control.Feedback type="invalid">Please enter a home address.</Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                            <Col lg={6} sm={12}>
                                                <Form.Group controlId={`addressLine2_${index}`}>
                                                    <Form.Label>Home address line 2</Form.Label>
                                                    <Form.Control type="input" maxLength={40} value={owner.addressLine2 ?? ""} onChange={(e: any) => updateOwner(index, { addressLine2: e.target.value })} placeholder="Enter address line 2" />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col lg={6}>
                                                <Form.Group controlId={`city_${index}`}>
                                                    <Form.Label><RequiredIcon />City</Form.Label>
                                                    <Form.Control required type="input" maxLength={28} value={owner.city} onChange={(e: any) => updateOwner(index, { city: e.target.value })} placeholder="Enter city" />
                                                    <Form.Control.Feedback type="invalid">Please enter a city.</Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                            <Col lg={4}>
                                                <Form.Group controlId={`state_${index}`}>
                                                    <Form.Label><RequiredIcon />State</Form.Label>
                                                    <StateDropdown id={`state_${index}`} required value={owner.state} onChange={(e: any) => updateOwner(index, { state: e.target.value })} />
                                                    <Form.Control.Feedback type="invalid">Please select a state.</Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                            <Col lg={2}>
                                                <Form.Group controlId={`zipCode_${index}`}>
                                                    <Form.Label><RequiredIcon />Zip code</Form.Label>
                                                    <InputMask required pattern="[0-9]{5}" mask="99999" className="form-control" value={owner.zipCode} onChange={(e: any) => updateOwner(index, { zipCode: e.target.value })} placeholder="Enter zip code" />
                                                    <Form.Control.Feedback type="invalid">Please enter a zip code.</Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Accordion.Collapse>
                            </Card>
                        ))}
                    </Accordion>
                    <div style={{ marginTop: '15px' }}>
                        <Button variant="outline-secondary" type="button" onClick={addOwner} disabled={owners.length >= MAX_OWNERS}>+ Add another owner</Button>
                        {owners.length >= MAX_OWNERS && <span style={{ marginLeft: '10px' }}>Maximum of {MAX_OWNERS} owners reached.</span>}
                    </div>
                    <div style={{ marginTop: '15px' }}>
                        <strong>Total ownership: {totalPercentage}%</strong>
                        {totalPercentage > 100 && <span style={{ color: 'red', marginLeft: '10px' }}>Cannot exceed 100%</span>}
                    </div>
                    {error && <div style={{ color: 'red', marginTop: '10px' }}>{error}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" type="button" onClick={onHide} disabled={isSaving}>Cancel</Button>
                    <Button type="submit" disabled={isSaving || totalPercentage > 100}>
                        {isSaving ? "Saving..." : (mode === 'sync' ? "Save" : "Save & Onboard")}
                        {isSaving && <Spinner as="span" animation="border" size="sm" className="margin-left-3" />}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default ControlOwnersModal;
